import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import { getGroupsList } from "../../../services/groupServices";
import { getAdherentsByGroup } from "../../../services/adherentServices";
import { getSettings } from "../../../services/settingsServices";
import {
  getAllAttendances,
  setAttendance as apiSetAttendance,
  clearAttendance as apiClearAttendance,
} from "../../../services/attendanceServices";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import FiltersBar from "./FiltersBar";
import AttendanceHeatmap from "./AttendanceHeatmap";
import StatStrip from "./StatStrip";
import Legend from "./Legend";
import { daysArray, generateSeasonMonths } from "./helpers";
import ChartsPanel from "./ChartsPanel";
import { toast } from "react-toastify";
import { BsClipboardCheckFill } from "react-icons/bs";

const AttendanceTable = () => {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [groups, setGroups] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(""); // "YYYY-MM"

  const [adherents, setAdherents] = useState([]);
  const [allAttendances, setAllAttendances] = useState(null); // chargé une fois quand nécessaire
  const [loading, setLoading] = useState(false);

  const [showCharts, setShowCharts] = useState(false);
  const [seasonActive, setSeasonActive] = useState("");

  // tableRows = [{ id, nom, prenom, dates: { "01": true/false }, totalPresent, totalAbsent }]
  const [tableRows, setTableRows] = useState([]);

  // Initial: groupes actifs + mois de la saison
  useEffect(() => {
    (async () => {
      try {
        const g = await getGroupsList();
        setGroups((g || []).filter((x) => x.is_active));

        const settings = await getSettings();
        const season = settings?.[0]?.saison_active || "2024-2025";
        setMonths(generateSeasonMonths(season));
        setSeasonActive(season);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  // Charger les adhérents du groupe sélectionné
  useEffect(() => {
    if (!selectedGroupId) {
      setAdherents([]);
      setTableRows([]);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const list = await getAdherentsByGroup(selectedGroupId);
        setAdherents(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedGroupId]);

  // Fabrique les lignes du tableau à partir des présences
  const buildRows = (month, adherentsList, attendancesList) => {
    if (!month || !Array.isArray(adherentsList)) return [];
    const days = daysArray(month); // ["01","02",...]
    const monthAttendance = (attendancesList || []).filter((a) =>
      String(a.date_presence).startsWith(month),
    );

    // Map: adherentId -> { day -> true/false }
    const map = {};
    for (const a of monthAttendance) {
      const day = new Date(a.date_presence).toISOString().slice(8, 10);
      if (!map[a.adherent_id]) map[a.adherent_id] = {};
      map[a.adherent_id][day] = !!a.presence;
    }

    return adherentsList.map((ad) => {
      let totalPresent = 0;
      let totalAbsent = 0;
      const daysMap = {};
      for (const d of days) {
        const v = map[ad.id]?.[d];
        if (v === true) totalPresent += 1;
        else if (v === false) totalAbsent += 1;
        daysMap[d] = v; // true/false/undefined
      }
      return {
        id: ad.id,
        nom: ad.nom || "",
        prenom: ad.prenom || "",
        dates: daysMap,
        totalPresent,
        totalAbsent,
      };
    });
  };

  // Calcul via bouton “Afficher” (optionnel)
  const handleShow = async () => {
    if (!selectedGroupId || !selectedMonth) return;
    try {
      setLoading(true);
      let data = allAttendances;
      if (!data) {
        data = await getAllAttendances(); // charge une seule fois
        setAllAttendances(data || []);
      }
      setTableRows(buildRows(selectedMonth, adherents, data));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Auto-rafraîchissement : dès que le groupe, le mois ou la liste des adhérents change
  useEffect(() => {
    const autoCompute = async () => {
      if (!selectedGroupId || !selectedMonth || adherents.length === 0) {
        setTableRows([]);
        return;
      }
      try {
        setLoading(true);
        let data = allAttendances;
        if (!data) {
          data = await getAllAttendances(); // première fois seulement
          setAllAttendances(data || []);
        }
        setTableRows(buildRows(selectedMonth, adherents, data));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    autoCompute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, selectedMonth, adherents]);

  // Si allAttendances arrive plus tard, recalcule
  useEffect(() => {
    if (
      allAttendances &&
      selectedGroupId &&
      selectedMonth &&
      adherents.length
    ) {
      setTableRows(buildRows(selectedMonth, adherents, allAttendances));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAttendances]);

  // KPIs groupe
  const kpis = useMemo(() => {
    const p = tableRows.reduce((sum, r) => sum + r.totalPresent, 0);
    const a = tableRows.reduce((sum, r) => sum + r.totalAbsent, 0);
    const totalMarked = p + a;
    const rate = totalMarked > 0 ? Math.round((p / totalMarked) * 100) : 0;
    return { presents: p, absents: a, rate };
  }, [tableRows]);

  // IDs “meilleure assiduité” (ex æquo possibles)
  const highlightIds = useMemo(() => {
    if (!tableRows.length) return [];
    const maxPresent = Math.max(...tableRows.map((r) => r.totalPresent || 0));
    if (maxPresent <= 0) return [];
    return tableRows
      .filter((r) => (r.totalPresent || 0) === maxPresent)
      .map((r) => r.id);
  }, [tableRows]);

  // === Nouveau : édition directe d'une cellule ===
  const handleToggleCell = async (adherentId, day, currentValue) => {
    if (!selectedMonth) return;
    const dateISO = `${selectedMonth}-${day}`; // "YYYY-MM-DD"

    // Détermine l'état suivant : undefined -> true, true -> false, false -> delete
    const next =
      currentValue === true
        ? "ABSENT"
        : currentValue === false
          ? "CLEAR"
          : "PRESENT";

    // Optimiste : met à jour allAttendances puis tableRows
    const prevAll = allAttendances || [];
    const iso = dateISO;
    const keyMatch = (a) =>
      a.adherent_id === adherentId &&
      String(a.date_presence).slice(0, 10) === iso;

    let optimisticAll;
    if (next === "PRESENT") {
      optimisticAll = [
        ...prevAll.filter((a) => !keyMatch(a)),
        { adherent_id: adherentId, presence: true, date_presence: iso },
      ];
    } else if (next === "ABSENT") {
      optimisticAll = [
        ...prevAll.filter((a) => !keyMatch(a)),
        { adherent_id: adherentId, presence: false, date_presence: iso },
      ];
    } else {
      // CLEAR
      optimisticAll = prevAll.filter((a) => !keyMatch(a));
    }

    // Applique l'optimiste
    setAllAttendances(optimisticAll);
    setTableRows(buildRows(selectedMonth, adherents, optimisticAll));

    try {
      if (next === "PRESENT") {
        await apiSetAttendance(adherentId, iso, true);
      } else if (next === "ABSENT") {
        await apiSetAttendance(adherentId, iso, false);
      } else {
        await apiClearAttendance(adherentId, iso);
      }
    } catch (e) {
      // Rollback en cas d'erreur
      console.error(e);
      toast.error("Échec de la mise à jour de la case.");
      setAllAttendances(prevAll);
      setTableRows(buildRows(selectedMonth, adherents, prevAll));
    }
  };

  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Enseignant"
          roleId="3"
          title="Tableau de présences"
          subtitle="Visualisez et gérez les présences de vos groupes par mois."
          icon={BsClipboardCheckFill}
          align="left"
          className="mb-4"
        />
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          {/* Bandeau */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 text-white">
            <h2 className="text-lg sm:text-xl font-semibold text-center">
              Visualisez les présences / absences par groupe et par mois
            </h2>
            <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
              Sélectionnez le groupe et le mois. Le tableau se met à jour
              automatiquement. Le/la plus assidu·e est mis·e en évidence.
            </p>
            <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
              Vous pouvez désormais cliquer une case du tableau pour basculer
              Présent → Absent → Non saisi (mise à jour immédiate).
            </p>
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={() => setShowCharts((v) => !v)}
                className="inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 px-4 py-2 ring-1 ring-white/50"
              >
                {showCharts ? "Masquer les graphiques" : "Graphiques"}
              </button>
            </div>
          </div>

          {/* Filtres */}
          <div className="px-5 py-6 sm:px-8">
            <FiltersBar
              groups={groups}
              months={months}
              selectedGroupId={selectedGroupId}
              setSelectedGroupId={setSelectedGroupId}
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              loading={loading}
              onShow={handleShow} // optionnel
            />

            {/* Panneau des graphiques (toggle) */}
            {showCharts && (
              <ChartsPanel
                isOpen={showCharts}
                seasonActive={seasonActive}
                selectedGroup={groups.find(
                  (g) => String(g.id) === String(selectedGroupId),
                )}
                adherents={adherents}
                allAttendances={allAttendances}
                setAllAttendances={setAllAttendances}
              />
            )}

            {/* Stats rapides */}
            {tableRows.length > 0 && (
              <div className="mt-4">
                <StatStrip
                  presents={kpis.presents}
                  absents={kpis.absents}
                  rate={kpis.rate}
                />
              </div>
            )}

            {/* Légende */}
            {tableRows.length > 0 && (
              <div className="mt-4">
                <Legend />
              </div>
            )}

            {/* Tableau / Heatmap */}
            <div className="mt-4">
              <AttendanceHeatmap
                month={selectedMonth}
                rows={tableRows}
                loading={loading}
                highlightIds={highlightIds}
                onToggleCell={handleToggleCell} // <<< édition directe
              />
            </div>

            {/* État vide */}
            {!loading && tableRows.length === 0 && (
              <div className="text-center py-10 text-slate-600 dark:text-slate-300">
                Sélectionnez un groupe et un mois pour afficher les présences.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AttendanceTable;
